import { authenticatedFetch } from './api';

/**
 * Fetches all teams from the backend.
 * @param {Object} params - Optional query parameters { limit, offset, search, sortBy, sortOrder }
 * @returns {Promise<Array>} A list of team objects.
 */
export const fetchTeams = async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/teams?${queryString}` : '/teams';
    
    const response = await authenticatedFetch(url);
    return response.data || response;
};

/**
 * Fetches a single team by ID.
 * @param {number} id - The ID of the team to fetch.
 * @returns {Promise<Object>} The team object.
 */
export const fetchTeamById = async (id) => {
    if (!id) {
        throw new Error('Team ID is required');
    }
    const response = await authenticatedFetch(`/teams/${id}`);
    return response.data || response;
};

/**
 * Creates a new team.
 * @param {object} teamData - Data for the new team { name, description, team_lead_id }.
 * @returns {Promise<Object>} The newly created team object.
 */
export const createTeam = async (teamData) => {
    if (!teamData || !teamData.name) {
        throw new Error('Team name is required');
    }

    const response = await authenticatedFetch('/teams', {
        method: 'POST',
        body: JSON.stringify(teamData),
    });
    
    return response.data || response;
};

/**
 * Updates an existing team.
 * @param {number} id - The ID of the team to update.
 * @param {object} teamData - The updated data for the team.
 * @returns {Promise<Object>} The updated team object.
 */
export const updateTeam = async (id, teamData) => {
    if (!id) {
        throw new Error('Team ID is required');
    }
    
    if (!teamData || Object.keys(teamData).length === 0) {
        throw new Error('Team data is required for update');
    }

    const response = await authenticatedFetch(`/teams/${id}`, {
        method: 'PUT',
        body: JSON.stringify(teamData),
    });
    
    return response.data || response;
};

/**
 * Deletes a team by its ID.
 * @param {number} id - The ID of the team to delete.
 * @returns {Promise<Object>} The deletion response.
 */
export const deleteTeam = async (id) => {
    if (!id) {
        throw new Error('Team ID is required');
    }

    const response = await authenticatedFetch(`/teams/${id}`, {
        method: 'DELETE',
    });
    
    return response.data || response;
};

/**
 * Fetches all users belonging to a specific team.
 * @param {number} teamId - The ID of the team.
 * @returns {Promise<Array>} A list of user objects in the team.
 */
export const getUsersInTeam = async (teamId) => {
    if (!teamId) {
        throw new Error('Team ID is required');
    }

    const response = await authenticatedFetch(`/teams/${teamId}/users`);
    return response.data || response;
};

/**
 * Alias for getUsersInTeam - for backwards compatibility
 */
export const getTeamMembers = async (teamId) => {
    return await getUsersInTeam(teamId);
};

/**
 * Adds a user to a team.
 * @param {number} teamId - The ID of the team.
 * @param {string} userId - The ID of the user to add.
 * @returns {Promise<Object>} The response object.
 */
export const addUserToTeam = async (teamId, userId) => {
    if (!teamId || !userId) {
        throw new Error('Team ID and User ID are required');
    }

    const response = await authenticatedFetch(`/teams/${teamId}/users`, {
        method: 'POST',
        body: JSON.stringify({ userId }),
    });
    
    return response.data || response;
};

/**
 * Alias for addUserToTeam - for backwards compatibility
 */
export const addMemberToTeam = async (teamId, userId) => {
    return await addUserToTeam(teamId, userId);
};

/**
 * Removes a user from a team.
 * @param {number} teamId - The ID of the team.
 * @param {string} userId - The ID of the user to remove.
 * @returns {Promise<Object>} The response object.
 */
export const removeUserFromTeam = async (teamId, userId) => {
    if (!teamId || !userId) {
        throw new Error('Team ID and User ID are required');
    }

    const response = await authenticatedFetch(`/teams/${teamId}/users/${userId}`, {
        method: 'DELETE',
    });
    
    return response.data || response;
};

/**
 * Alias for removeUserFromTeam - for backwards compatibility
 */
export const removeMemberFromTeam = async (teamId, userId) => {
    return await removeUserFromTeam(teamId, userId);
};

/**
 * Fetches team statistics.
 * @returns {Promise<Object>} Team statistics object.
 */
export const fetchTeamStats = async () => {
    const response = await authenticatedFetch('/teams/stats');
    return response.data || response;
};

/**
 * Searches for teams by query string.
 * @param {string} query - The search query.
 * @param {number} limit - Optional limit for results (default 10).
 * @returns {Promise<Array>} A list of matching team objects.
 */
export const searchTeams = async (query, limit = 10) => {
    if (!query || query.trim() === '') {
        return [];
    }

    const response = await fetchTeams({ search: query, limit });
    return response.data || response;
};

/**
 * Bulk adds multiple users to a team.
 * @param {number} teamId - The ID of the team.
 * @param {Array<string>} userIds - Array of user IDs to add.
 * @returns {Promise<Object>} The response object.
 */
export const bulkAddUsersToTeam = async (teamId, userIds) => {
    if (!teamId || !Array.isArray(userIds) || userIds.length === 0) {
        throw new Error('Team ID and array of User IDs are required');
    }

    const response = await authenticatedFetch(`/teams/${teamId}/users/bulk`, {
        method: 'POST',
        body: JSON.stringify({ userIds }),
    });
    
    return response.data || response;
};

/**
 * Checks if a user is a member of a team.
 * @param {number} teamId - The ID of the team.
 * @param {string} userId - The ID of the user.
 * @returns {Promise<boolean>} True if user is a member, false otherwise.
 */
export const isUserInTeam = async (teamId, userId) => {
    if (!teamId || !userId) {
        throw new Error('Team ID and User ID are required');
    }

    try {
        const members = await getUsersInTeam(teamId);
        return Array.isArray(members) && members.some(member => String(member.id) === String(userId)) /* --- UPDATE: ids arrive as numbers or strings */;
    } catch (error) {
        console.error('Error checking team membership:', error);
        return false;
    }
};

/**
 * Gets teams that a specific user belongs to.
 * @param {string} userId - The ID of the user.
 * @returns {Promise<Array>} A list of team objects the user belongs to.
 */
export const getTeamsByUser = async (userId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    const response = await authenticatedFetch(`/users/${userId}/teams`);
    return response.data || response;
};

/**
 * Validates team data before submission.
 * @param {Object} teamData - The team data to validate.
 * @returns {Object} Validation result { valid: boolean, errors: Array<string> }
 */
export const validateTeamData = (teamData) => {
    const errors = [];

    if (!teamData) {
        errors.push('Team data is required');
        return { valid: false, errors };
    }

    if (!teamData.name || teamData.name.trim() === '') {
        errors.push('Team name is required');
    } else if (teamData.name.length < 3) {
        errors.push('Team name must be at least 3 characters long');
    } else if (teamData.name.length > 255) {
        errors.push('Team name must not exceed 255 characters');
    }

    if (teamData.description && teamData.description.length > 1000) {
        errors.push('Description must not exceed 1000 characters');
    }

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Exports all team data to JSON format.
 * @returns {Promise<Object>} All teams with their members.
 */
export const exportTeamsData = async () => {
    const teams = await fetchTeams({ limit: 1000 });
    const teamsWithMembers = await Promise.all(
        teams.map(async (team) => {
            try {
                const members = await getUsersInTeam(team.id);
                return { ...team, members };
            } catch (error) {
                console.error(`Error fetching members for team ${team.id}:`, error);
                return { ...team, members: [] };
            }
        })
    );
    
    return teamsWithMembers;
};

export default {
    fetchTeams,
    fetchTeamById,
    createTeam,
    updateTeam,
    deleteTeam,
    getUsersInTeam,
    getTeamMembers,
    addUserToTeam,
    addMemberToTeam,
    removeUserFromTeam,
    removeMemberFromTeam,
    fetchTeamStats,
    searchTeams,
    bulkAddUsersToTeam,
    isUserInTeam,
    getTeamsByUser,
    validateTeamData,
    exportTeamsData
};